import React from "react";
import styled from "@emotion/styled";
import Button from "../../Common/Button/Button";
import {
  formatDate,
  formatNotificationBody,
} from "../../Common/Common-Function/CommonFunctions";
import NewApplicationIcon from "../../Assets/Images/new-application-icon.svg";

const formatType = (type) => {
  const typeMap = {
    application_received: "APPLICATIONS",
  };
  return typeMap[type] || type;
};

const formatRelativeTime = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now - date) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
  if (diffInHours < 24)
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  if (diffInDays < 7)
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
  return formatDate(dateString);
};

const Card = styled("div", {
  shouldForwardProp: (prop) => prop !== "isDarkMode" && prop !== "read_at",
})(({ read_at, isDarkMode }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 20px",
  background: !read_at
    ? isDarkMode
      ? "linear-gradient(90deg, rgba(104, 96, 238, 0.18), var(--card__background))"
      : "#F8F7FF"
    : "var(--card__background)",
  border: "1px solid var(--border__color)",
  borderLeft: !read_at
    ? "4px solid var(--primary__color)"
    : "1px solid var(--border__color)",
  borderRadius: "12px",
  gap: "16px",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: isDarkMode
      ? "0 8px 20px rgba(0, 0, 0, 0.22)"
      : "0 2px 8px rgba(0,0,0,0.06)",
  },
  "@media (max-width: 768px)": {
    alignItems: "flex-start",
    flexDirection: "column",
  },
  "@media (max-width: 480px)": {
    padding: "14px 12px",
    gap: "12px",
  },
}));

const LeftSection = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flex: 1,
  minWidth: 0,
  "@media (max-width: 480px)": {
    alignItems: "flex-start",
    gap: "8px",
    width: "100%",
  },
});

const UnreadDot = styled("span")({
  width: "8px",
  height: "8px",
  minWidth: "8px",
  borderRadius: "50%",
  background: "var(--primary__color)",
  boxShadow: "0 0 0 3px rgba(104, 96, 238, 0.16)",
  position: "absolute",
  left: "-14px",
  top: "50%",
  transform: "translateY(-50%)",
});

const IconContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "isDarkMode",
})(({ isDarkMode }) => ({
  width: "44px",
  height: "44px",
  minWidth: "44px",
  borderRadius: "50%",
  background: isDarkMode ? "rgba(104, 96, 238, 0.22)" : "#EEF2FF",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  "@media (max-width: 480px)": {
    width: "38px",
    height: "38px",
    minWidth: "38px",
  },
}));

const Icon = styled("img")({
  width: "44px",
  height: "44px",
  objectFit: "contain",
  "@media (max-width: 480px)": {
    width: "38px",
    height: "38px",
  },
});

const Content = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flex: 1,
  minWidth: 0,
});

const Title = styled("div")({
  fontSize: "14px",
  fontWeight: 600,
  color: "var(--header__color)",
  lineHeight: "1.4",
});

const Body = styled("div")({
  fontSize: "13px",
  color: "var(--job-Description_Color)",
  lineHeight: "1.4",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  overflowWrap: "anywhere",
  "@media (max-width: 768px)": {
    whiteSpace: "normal",
  },
});

const BottomRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "2px",
  flexWrap: "wrap",
});

const TypeChip = styled("div")({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "2px 10px",
  border: "1px solid var(--border__color)",
  borderRadius: "999px",
  fontSize: "11px",
  fontWeight: 500,
  color: "var(--job-Description_Color)",
  textTransform: "uppercase",
  letterSpacing: "0.3px",
  background: "var(--body__background)",
  "&::before": {
    content: '""',
    width: "6px",
    height: "6px",
    minWidth: "6px",
    borderRadius: "50%",
    background: "#22C55E",
  },
});

const RightSection = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: "10px",
  flexShrink: 0,
  "@media (max-width: 768px)": {
    alignItems: "flex-start",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  "@media (max-width: 480px)": {
    alignItems: "stretch",
    flexDirection: "column",
    gap: "8px",
  },
});

const Time = styled("span")({
  fontSize: "12px",
  color: "var(--job-Description_Color)",
  whiteSpace: "nowrap",
});

function NotificationCard({
  id,
  title,
  body,
  type,
  read_at,
  created_at,
  onMarkAsRead = () => {},
  isDarkMode = false,
}) {
  const isUnread = !read_at;
  const formattedBody = formatNotificationBody(body);
  return (
    <Card read_at={read_at} isDarkMode={isDarkMode}>
      <LeftSection>
        <IconContainer isDarkMode={isDarkMode}>
          {isUnread && <UnreadDot />}
          <Icon src={NewApplicationIcon} alt="new application" />
        </IconContainer>

        <Content>
          <Title>{title}</Title>
          <Body>{formattedBody}</Body>

          <BottomRow>
            <TypeChip>{formatType(type)}</TypeChip>
          </BottomRow>
        </Content>
      </LeftSection>

      <RightSection>
        <Time>{formatRelativeTime(created_at)}</Time>

        {isUnread && (
          <Button
            label="Mark as read"
            event={() => onMarkAsRead(id)}
            backgroundColor="var(--body__background)"
            color={
              isDarkMode ? " var(--header__color)" : "var(--page_Header_Color)"
            }
            border={`1px solid ${isDarkMode ? "var(--border__color)" : "var(--page_Header_Color)"}`}
            hoverd={false}
            style={{
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: 500,
            }}
          />
        )}
      </RightSection>
    </Card>
  );
}

export default NotificationCard;
